import { 
  CognitiveMetrics, 
  MovementPattern, 
  SpatialNavigationMetric, 
  DualTaskPerformance,
  CognitiveWarning,
  RiskLevel
} from '../interfaces/types';

// Calculate trend percentage between current and previous values
export const calculateTrendPercentage = (current: number, previous: number): number => {
  if (previous === 0) return 0;
  return Math.round(((current - previous) / previous) * 100);
};

// Format trend for display (e.g., "+5%", "-2%", "stable")
export const formatTrend = (trendPercent: number): string => {
  if (Math.abs(trendPercent) < 1) return 'stable';
  return trendPercent > 0 ? `+${trendPercent}%` : `${trendPercent}%`;
};

// Calculate average value for a metric over a set of data points
export const calculateAverage = (data: CognitiveMetrics[], metricKey: keyof CognitiveMetrics): number => {
  if (!data.length) return 0;
  const validData = data.filter(item => typeof item[metricKey] === 'number');
  if (!validData.length) return 0;
  
  const sum = validData.reduce((acc, item) => acc + (item[metricKey] as number), 0);
  return sum / validData.length;
};

// Analyze movement patterns to detect abnormalities
export const analyzeMovementPatterns = (patterns: MovementPattern[]): {
  abnormalPatterns: MovementPattern[];
  abnormalityScore: number;
  commonPaths: string[][];
  mostFrequentLocations: string[];
} => {
  // Filter for abnormal patterns
  const abnormalPatterns = patterns.filter(p => 
    p.patternType === 'ANOMALOUS' || p.deviationFromBaseline > 30
  );
  
  // Calculate overall abnormality score (0-100)
  const abnormalityPercentage = (abnormalPatterns.length / patterns.length) * 100;
  const deviationAverage = patterns.reduce((sum, p) => sum + p.deviationFromBaseline, 0) / patterns.length;
  const abnormalityScore = Math.min(100, (abnormalityPercentage * 0.5) + (deviationAverage * 0.5));
  
  // Find common paths (simplified approach)
  const pathStrings = patterns.map(p => p.locationPath.join('-'));
  const pathCounts: {[key: string]: number} = {};
  
  pathStrings.forEach(path => {
    pathCounts[path] = (pathCounts[path] || 0) + 1;
  });
  
  // Get the top 3 most common paths
  const commonPaths = Object.entries(pathCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([path]) => path.split('-'));
  
  // Find most frequent locations
  const locationCounts: {[key: string]: number} = {};
  
  patterns.forEach(pattern => {
    pattern.locationPath.forEach(location => {
      locationCounts[location] = (locationCounts[location] || 0) + 1;
    });
  });
  
  const mostFrequentLocations = Object.entries(locationCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([location]) => location);
  
  return {
    abnormalPatterns,
    abnormalityScore,
    commonPaths,
    mostFrequentLocations
  };
};

// Analyze spatial navigation metrics to assess efficiency and changes
export const analyzeSpatialNavigation = (metrics: SpatialNavigationMetric[]): {
  averageEfficiency: number;
  efficiencyTrend: number;
  problematicRoutes: {start: string; end: string; efficiency: number}[];
  hesitationRate: number;
} => {
  if (!metrics.length) {
    return {
      averageEfficiency: 0,
      efficiencyTrend: 0,
      problematicRoutes: [],
      hesitationRate: 0
    };
  }
  
  // Sort by timestamp (newest first)
  const sortedMetrics = [...metrics].sort((a, b) => 
    b.timestamp.getTime() - a.timestamp.getTime()
  );
  
  // Calculate average efficiency
  const averageEfficiency = sortedMetrics.reduce((sum, m) => sum + m.pathEfficiency, 0) / sortedMetrics.length;
  
  // Calculate efficiency trend (comparing newer half vs older half)
  const midpoint = Math.floor(sortedMetrics.length / 2);
  const recentMetrics = sortedMetrics.slice(0, midpoint);
  const olderMetrics = sortedMetrics.slice(midpoint);
  
  const recentAvg = recentMetrics.length ? 
    recentMetrics.reduce((sum, m) => sum + m.pathEfficiency, 0) / recentMetrics.length : 0;
  
  const olderAvg = olderMetrics.length ? 
    olderMetrics.reduce((sum, m) => sum + m.pathEfficiency, 0) / olderMetrics.length : 0;
  
  const efficiencyTrend = olderAvg ? calculateTrendPercentage(recentAvg, olderAvg) : 0;
  
  // Identify problematic routes (routes with below-average efficiency)
  const routeEfficiencies: {[key: string]: number[]} = {};
  
  sortedMetrics.forEach(metric => {
    const routeKey = `${metric.startLocation}-${metric.endLocation}`;
    if (!routeEfficiencies[routeKey]) {
      routeEfficiencies[routeKey] = [];
    }
    routeEfficiencies[routeKey].push(metric.pathEfficiency);
  });
  
  const problematicRoutes = Object.entries(routeEfficiencies)
    .map(([route, efficiencies]) => {
      const avgEfficiency = efficiencies.reduce((sum, e) => sum + e, 0) / efficiencies.length;
      const [start, end] = route.split('-');
      return { start, end, efficiency: avgEfficiency };
    })
    .filter(route => route.efficiency < averageEfficiency * 0.9) // 90% of average or worse
    .sort((a, b) => a.efficiency - b.efficiency); // Sort by ascending efficiency (worst first)
  
  // Calculate hesitation rate (average hesitations per navigation)
  const hesitationRate = sortedMetrics.reduce((sum, m) => sum + m.hesitations, 0) / sortedMetrics.length;
  
  return {
    averageEfficiency,
    efficiencyTrend,
    problematicRoutes,
    hesitationRate
  };
};

// Analyze dual task performance to assess cognitive load handling
export const analyzeDualTaskPerformance = (tasks: DualTaskPerformance[]): {
  averagePerformance: number;
  performanceTrend: number;
  attentionSwitchRate: number;
  errorRateTrend: number;
  taskTypePerformance: {taskType: string; performance: number}[];
} => {
  if (!tasks.length) {
    return {
      averagePerformance: 0,
      performanceTrend: 0,
      attentionSwitchRate: 0,
      errorRateTrend: 0,
      taskTypePerformance: []
    };
  }
  
  // Sort by timestamp (newest first)
  const sortedTasks = [...tasks].sort((a, b) => 
    b.timestamp.getTime() - a.timestamp.getTime()
  );
  
  // Calculate average overall performance
  const averagePerformance = sortedTasks.reduce((sum, t) => sum + t.overallPerformance, 0) / sortedTasks.length;
  
  // Calculate performance trend (comparing newer half vs older half)
  const midpoint = Math.floor(sortedTasks.length / 2);
  const recentTasks = sortedTasks.slice(0, midpoint);
  const olderTasks = sortedTasks.slice(midpoint);
  
  const recentPerformanceAvg = recentTasks.length ? 
    recentTasks.reduce((sum, t) => sum + t.overallPerformance, 0) / recentTasks.length : 0;
  
  const olderPerformanceAvg = olderTasks.length ? 
    olderTasks.reduce((sum, t) => sum + t.overallPerformance, 0) / olderTasks.length : 0;
  
  const performanceTrend = olderPerformanceAvg ? 
    calculateTrendPercentage(recentPerformanceAvg, olderPerformanceAvg) : 0;
  
  // Calculate average attention switches per minute
  const attentionSwitchRate = sortedTasks.reduce((sum, t) => {
    const durationMinutes = t.completionTime / 60;
    return sum + (t.attentionSwitches / durationMinutes);
  }, 0) / sortedTasks.length;
  
  // Calculate error rate trend
  const recentErrorAvg = recentTasks.length ? 
    recentTasks.reduce((sum, t) => sum + t.errorRate, 0) / recentTasks.length : 0;
  
  const olderErrorAvg = olderTasks.length ? 
    olderTasks.reduce((sum, t) => sum + t.errorRate, 0) / olderTasks.length : 0;
  
  // For error rate, a positive trend is bad (more errors), so we invert the sign
  const errorRateTrend = olderErrorAvg ? 
    -1 * calculateTrendPercentage(recentErrorAvg, olderErrorAvg) : 0;
  
  // Calculate performance by primary task type
  const taskPerformanceMap: {[key: string]: number[]} = {};
  
  sortedTasks.forEach(task => {
    if (!taskPerformanceMap[task.primaryTask]) {
      taskPerformanceMap[task.primaryTask] = [];
    }
    taskPerformanceMap[task.primaryTask].push(task.primaryTaskPerformance);
  });
  
  const taskTypePerformance = Object.entries(taskPerformanceMap)
    .map(([taskType, performances]) => ({
      taskType,
      performance: performances.reduce((sum, p) => sum + p, 0) / performances.length
    }))
    .sort((a, b) => b.performance - a.performance); // Sort by performance (best first)
  
  return {
    averagePerformance,
    performanceTrend,
    attentionSwitchRate,
    errorRateTrend,
    taskTypePerformance
  };
};

// Calculate overall cognitive risk level based on metrics and warnings
export const calculateCognitiveRiskLevel = (
  metrics: CognitiveMetrics[],
  warnings: CognitiveWarning[],
  movementPatterns: MovementPattern[],
  navigationMetrics: SpatialNavigationMetric[],
  dualTaskPerformances: DualTaskPerformance[]
): {
  riskLevel: RiskLevel;
  score: number;
  factors: string[];
} => {
  if (!metrics.length) {
    return { riskLevel: 'MEDIUM', score: 50, factors: ['Insufficient data'] };
  }
  
  // Sort metrics by date (newest first)
  const sortedMetrics = [...metrics].sort((a, b) => 
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  
  // Get most recent metrics
  const latestMetrics = sortedMetrics[0];
  
  // Calculate base score from key metrics (0-100)
  const baseScore = (
    latestMetrics.navigationScore * 0.25 + 
    latestMetrics.memoryScore * 0.25 + 
    latestMetrics.executiveFunction * 0.2 +
    latestMetrics.dualtaskPerformance * 0.15 +
    latestMetrics.routineAdherence * 0.15
  );
  
  // Factor in trends (if declining, reduce score)
  let trendPenalty = 0;
  
  // Look at last 3 days of data if available
  const recentDays = sortedMetrics.slice(0, Math.min(3, sortedMetrics.length));
  if (recentDays.length > 1) {
    const first = recentDays[recentDays.length - 1];
    const last = recentDays[0];
    
    // Calculate average daily change for key metrics
    const navChange = (last.navigationScore - first.navigationScore) / recentDays.length;
    const memChange = (last.memoryScore - first.memoryScore) / recentDays.length;
    const execChange = (last.executiveFunction - first.executiveFunction) / recentDays.length;
    
    // Apply penalty for declining metrics
    if (navChange < -1) trendPenalty += Math.abs(navChange) * 0.5;
    if (memChange < -1) trendPenalty += Math.abs(memChange) * 0.5;
    if (execChange < -1) trendPenalty += Math.abs(execChange) * 0.5;
  }
  
  // Factor in active warnings
  const warningPenalty = warnings.reduce((penalty, warning) => {
    switch (warning.severity) {
      case 'CRITICAL': return penalty + 20;
      case 'HIGH': return penalty + 10;
      case 'MEDIUM': return penalty + 5;
      case 'LOW': return penalty + 2;
      default: return penalty;
    }
  }, 0);
  
  // Factor in movement pattern abnormalities
  const { abnormalityScore } = analyzeMovementPatterns(movementPatterns);
  const movementPenalty = abnormalityScore * 0.1; // Scale down the impact
  
  // Factor in spatial navigation efficiency
  const { averageEfficiency, efficiencyTrend } = analyzeSpatialNavigation(navigationMetrics);
  const navigationPenalty = (100 - averageEfficiency) * 0.05;
  
  // Add trend bonus/penalty
  const navigationTrendFactor = efficiencyTrend > 0 ? 2 : efficiencyTrend < 0 ? -2 : 0;
  
  // Factor in dual task performance
  const { averagePerformance, performanceTrend } = analyzeDualTaskPerformance(dualTaskPerformances);
  const dualTaskPenalty = (100 - averagePerformance) * 0.05;
  
  // Add trend bonus/penalty
  const dualTaskTrendFactor = performanceTrend > 0 ? 2 : performanceTrend < 0 ? -2 : 0;
  
  // Calculate final score
  let finalScore = baseScore - trendPenalty - warningPenalty - movementPenalty - navigationPenalty - dualTaskPenalty + navigationTrendFactor + dualTaskTrendFactor;
  
  // Ensure score is in valid range
  finalScore = Math.max(0, Math.min(100, finalScore));
  
  // Determine risk level based on score
  let riskLevel: RiskLevel;
  if (finalScore >= 85) riskLevel = 'LOW';
  else if (finalScore >= 70) riskLevel = 'MEDIUM';
  else if (finalScore >= 50) riskLevel = 'HIGH';
  else riskLevel = 'CRITICAL';
  
  // Collect contributing factors
  const factors: string[] = [];
  
  if (trendPenalty > 5) factors.push('Declining cognitive metrics');
  if (warningPenalty > 0) factors.push('Active cognitive warnings');
  if (movementPenalty > 5) factors.push('Abnormal movement patterns');
  if (navigationPenalty > 5) factors.push('Reduced spatial navigation efficiency');
  if (dualTaskPenalty > 5) factors.push('Reduced dual-task performance');
  if (latestMetrics.confusionEvents > 2) factors.push('Increased confusion events');
  
  return {
    riskLevel,
    score: Math.round(finalScore),
    factors
  };
}; 